import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as service from './service';

// Initial state
const initialState = {
    // Asset Types
    assetTypes: [],
    assetTypesLoading: false,
    assetTypesError: null,
    
    // Brands
    brands: [],
    brandsLoading: false,
    brandsError: null,
    
    // Models
    models: [],
    modelsLoading: false,
    modelsError: null,
    
    // Inventory Items
    items: [],
    itemsLoading: false,
    itemsError: null,
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    
    // Create/Update form
    formData: {
        itemName: '',
        description: '',
        assetType: '',
        brand: '',
        model: '',
        stock: 0
    },
    formErrors: {},
    isSubmitting: false
};

// Async thunks for Asset Types
export const fetchAssetTypes = createAsyncThunk(
    'inventory/fetchAssetTypes',
    async (_, { rejectWithValue }) => {
        try {
            const response = await service.getAssetTypes();
            // Response middleware wraps: { success: true, data: { message: "Ok", data: assetTypes } }
            // Axios interceptor returns response.data, so response is { success: true, data: { message: "Ok", data: assetTypes } }
            // So response.data.data.data is the array
            return response?.data?.data || response?.data || [];
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch asset types');
        }
    }
);

export const createAssetTypeThunk = createAsyncThunk(
    'inventory/createAssetType',
    async (name, { rejectWithValue }) => {
        try {
            const response = await service.createAssetType(name);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create asset type');
        }
    }
);

// Async thunks for Brands
export const fetchBrands = createAsyncThunk(
    'inventory/fetchBrands',
    async (assetType, { rejectWithValue }) => {
        try {
            const response = await service.getBrands(assetType);
            // Response middleware wraps: { success: true, data: { message: "Ok", data: brands } }
            // Axios interceptor returns response.data, so response is { success: true, data: { message: "Ok", data: brands } }
            // So response.data.data.data is the array
            return response?.data?.data || response?.data || [];
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch brands');
        }
    }
);

export const createBrandThunk = createAsyncThunk(
    'inventory/createBrand',
    async ({ name, assetTypes }, { rejectWithValue }) => {
        try {
            const response = await service.createBrand(name, assetTypes);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create brand');
        }
    }
);

// Async thunks for Models
export const fetchModels = createAsyncThunk(
    'inventory/fetchModels',
    async (brand, { rejectWithValue }) => {
        try {
            const response = await service.getModels(brand);
            // Response middleware wraps: { success: true, data: { message: "Ok", data: models } }
            // Axios interceptor returns response.data, so response is { success: true, data: { message: "Ok", data: models } }
            // So response.data.data.data is the array
            return response?.data?.data || response?.data || [];
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch models');
        }
    }
);

export const createModelThunk = createAsyncThunk(
    'inventory/createModel',
    async ({ name, brand }, { rejectWithValue }) => {
        try {
            const response = await service.createModel(name, brand);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create model');
        }
    }
);

// Async thunks for Inventory Items
export const fetchInventoryItems = createAsyncThunk(
    'inventory/fetchInventoryItems',
    async (queryParams, { rejectWithValue }) => {
        try {
            const response = await service.getInventoryItems(queryParams);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch inventory items');
        }
    }
);

export const createInventoryItem = createAsyncThunk(
    'inventory/createInventoryItem',
    async (itemData, { rejectWithValue }) => {
        try {
            const response = await service.createInventoryItem(itemData);
            // Response middleware wraps: { success: true, data: { message: "Ok", data: item } }
            // Axios interceptor returns response.data, so response is { success: true, data: { message: "Ok", data: item } }
            return response?.data?.data || response?.data || response;
        } catch (error) {
            // handleApiError returns error.response.data, which is { message: "...", error: ... }
            const errorMessage = error?.message || error?.error?.message || error?.response?.data?.message || 'Failed to create inventory item';
            return rejectWithValue(errorMessage);
        }
    }
);

export const updateInventoryItem = createAsyncThunk(
    'inventory/updateInventoryItem',
    async ({ id, itemData }, { rejectWithValue }) => {
        try {
            const response = await service.updateInventoryItem(id, itemData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update inventory item');
        }
    }
);

export const deleteInventoryItem = createAsyncThunk(
    'inventory/deleteInventoryItem',
    async (id, { rejectWithValue }) => {
        try {
            await service.deleteInventoryItem(id);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete inventory item');
        }
    }
);

export const fetchInventoryForExport = createAsyncThunk(
    'inventory/fetchInventoryForExport',
    async (queryParams, { rejectWithValue }) => {
        try {
            const response = await service.getInventoryForExport(queryParams);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch inventory for export');
        }
    }
);

export const fetchUsageHistoryForExport = createAsyncThunk(
    'inventory/fetchUsageHistoryForExport',
    async (id, { rejectWithValue }) => {
        try {
            const response = await service.getUsageHistoryForExport(id);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch usage history for export');
        }
    }
);

// Slice
const inventorySlice = createSlice({
    name: 'inventory',
    initialState,
    reducers: {
        updateFormField: (state, action) => {
            const { field, value } = action.payload;
            state.formData[field] = value;
            // Clear error for this field when user types
            if (state.formErrors[field]) {
                delete state.formErrors[field];
            }
        },
        resetForm: (state) => {
            state.formData = initialState.formData;
            state.formErrors = {};
        },
        setFormErrors: (state, action) => {
            state.formErrors = action.payload;
        },
        clearBrands: (state) => {
            state.brands = [];
        },
        clearModels: (state) => {
            state.models = [];
        }
    },
    extraReducers: (builder) => {
        // Asset Types
        builder
            .addCase(fetchAssetTypes.pending, (state) => {
                state.assetTypesLoading = true;
                state.assetTypesError = null;
            })
            .addCase(fetchAssetTypes.fulfilled, (state, action) => {
                state.assetTypesLoading = false;
                state.assetTypes = action.payload;
            })
            .addCase(fetchAssetTypes.rejected, (state, action) => {
                state.assetTypesLoading = false;
                state.assetTypesError = action.payload;
            })
            .addCase(createAssetTypeThunk.fulfilled, (state, action) => {
                state.assetTypes.push(action.payload);
            });

        // Brands
        builder
            .addCase(fetchBrands.pending, (state) => {
                state.brandsLoading = true;
                state.brandsError = null;
            })
            .addCase(fetchBrands.fulfilled, (state, action) => {
                state.brandsLoading = false;
                // Extract data from response: action.payload is { message: "Ok", data: brands }
                state.brands = action.payload?.data?.data || action.payload?.data || action.payload || [];
            })
            .addCase(fetchBrands.rejected, (state, action) => {
                state.brandsLoading = false;
                state.brandsError = action.payload;
            })
            .addCase(createBrandThunk.fulfilled, (state, action) => {
                if (Array.isArray(state.brands)) {
                    const newBrand = action.payload?.data || action.payload;
                    state.brands.push(newBrand);
                }
            });

        // Models
        builder
            .addCase(fetchModels.pending, (state) => {
                state.modelsLoading = true;
                state.modelsError = null;
            })
            .addCase(fetchModels.fulfilled, (state, action) => {
                state.modelsLoading = false;
                state.models = action.payload;
            })
            .addCase(fetchModels.rejected, (state, action) => {
                state.modelsLoading = false;
                state.modelsError = action.payload;
            })
            .addCase(createModelThunk.fulfilled, (state, action) => {
                if (Array.isArray(state.models)) {
                    const newModel = action.payload?.data || action.payload;
                    state.models.push(newModel);
                }
            });

        // Inventory Items
        builder
            .addCase(fetchInventoryItems.pending, (state) => {
                state.itemsLoading = true;
                state.itemsError = null;
            })
            .addCase(fetchInventoryItems.fulfilled, (state, action) => {
                state.itemsLoading = false;
                state.items = action.payload.data;
                state.currentPage = action.payload.page;
                state.totalPages = action.payload.totalPages;
                state.totalCount = action.payload.totalCount;
            })
            .addCase(fetchInventoryItems.rejected, (state, action) => {
                state.itemsLoading = false;
                state.itemsError = action.payload;
            })
            .addCase(createInventoryItem.pending, (state) => {
                state.isSubmitting = true;
            })
            .addCase(createInventoryItem.fulfilled, (state) => {
                state.isSubmitting = false;
                state.formData = initialState.formData;
                state.formErrors = {};
            })
            .addCase(createInventoryItem.rejected, (state, action) => {
                state.isSubmitting = false;
            })
            .addCase(updateInventoryItem.pending, (state) => {
                state.isSubmitting = true;
            })
            .addCase(updateInventoryItem.fulfilled, (state) => {
                state.isSubmitting = false;
                state.formData = initialState.formData;
                state.formErrors = {};
            })
            .addCase(updateInventoryItem.rejected, (state) => {
                state.isSubmitting = false;
            })
            .addCase(deleteInventoryItem.fulfilled, (state, action) => {
                state.items = state.items.filter(item => item._id !== action.payload);
            });
    }
});

export const { updateFormField, resetForm, setFormErrors, clearBrands, clearModels } = inventorySlice.actions;
export default inventorySlice.reducer;

